/**
 * Legend and display utility functions for Atlas visualizations.
 *
 * These functions map color maps, product classifications, and view modes
 * to legend categories, labels, and sorting orders used by chart components.
 *
 * Moved from @atlas-int/data (colorMaps.ts) because they are
 * visualization-specific and do not belong in the data layer.
 */

import {
  groupedRegionColorMap,
  groupNames,
  hs92ColorsMap,
  hs12ColorsMap,
  hs22ColorsMap,
  sitcColorsMap,
  clusterColorsMap,
  GREEN_PRODUCT_HIGH_RCA,
  NOT_GREEN_PRODUCT_HIGH_RCA,
  GREEN_PRODUCT_LOW_RCA,
  NOT_GREEN_PRODUCT_LOW_RCA,
} from "@atlas-int/tokens/classifications";
import {
  ProductClass,
  servicesPartnersGroupId,
  getClassificationNamesMap,
  getClassificationSortingOrder,
  getClassificationServicesCategory,
  ColorBy, LegendLabels, UIView,
} from "@atlas-int/data";
import type { CategoryDatum } from "./components/LegendItem";

// -- Color maps by product class (for getColorMap) --

const productClassColorMaps: Record<string, Map<string, string>> = {
  [ProductClass.HS92Products]: hs92ColorsMap,
  [ProductClass.HS12Products]: hs12ColorsMap,
  [ProductClass.HS22Products]: hs22ColorsMap,
  [ProductClass.SITCProducts]: sitcColorsMap,
};

export const getLegendCategories = (rcaThreshold: number): CategoryDatum[] => [
  {
    id: "green-high-rca",
    color: GREEN_PRODUCT_HIGH_RCA,
    name: `Green\u00A0Products\u00A0(RCA>${rcaThreshold})`,
  },
  {
    id: "not-green-high-rca",
    color: NOT_GREEN_PRODUCT_HIGH_RCA,
    name: `NOT\u00A0Green\u00A0Products\u00A0(RCA>${rcaThreshold})`,
  },
  {
    id: "green-low-rca",
    color: GREEN_PRODUCT_LOW_RCA,
    name: `Green\u00A0Products\u00A0(RCA<${rcaThreshold})`,
  },
  {
    id: "not-green-low-rca",
    color: NOT_GREEN_PRODUCT_LOW_RCA,
    name: `NOT\u00A0Green\u00A0Products\u00A0(RCA<${rcaThreshold})`,
  },
];

// -- Legend config per product class (delegates to registry for names/services) --

export const productClassLegendConfig: Record<string, {
  colorMap: Map<string, string>;
  namesMap: Map<string, string>;
  servicesCategory: string;
}> = {
  [ProductClass.HS92Products]: { colorMap: hs92ColorsMap, namesMap: getClassificationNamesMap(ProductClass.HS92Products), servicesCategory: getClassificationServicesCategory(ProductClass.HS92Products)! },
  [ProductClass.HS12Products]: { colorMap: hs12ColorsMap, namesMap: getClassificationNamesMap(ProductClass.HS12Products), servicesCategory: getClassificationServicesCategory(ProductClass.HS12Products)! },
  [ProductClass.HS22Products]: { colorMap: hs22ColorsMap, namesMap: getClassificationNamesMap(ProductClass.HS22Products), servicesCategory: getClassificationServicesCategory(ProductClass.HS22Products)! },
  [ProductClass.SITCProducts]: { colorMap: sitcColorsMap, namesMap: getClassificationNamesMap(ProductClass.SITCProducts), servicesCategory: getClassificationServicesCategory(ProductClass.SITCProducts)! },
};

// -- Color/legend utility functions --

export const colorByToLegendLabel: Record<ColorBy, LegendLabels> = {
  [ColorBy.Sector]: LegendLabels.Sector,
  [ColorBy.Green]: LegendLabels.Green,
  [ColorBy.Complexity]: LegendLabels.Complexity,
  [ColorBy.Cluster]: LegendLabels.Cluster,
};

export const getColorMap = ({ view, productClass }: { view: UIView; productClass: ProductClass }) => {
  if (view === UIView.Markets) {
    return groupedRegionColorMap;
  } else if (view === UIView.Products) {
    return productClassColorMaps[productClass];
  } else {
    return undefined;
  }
};

/**
 * Get the sector names map for a product classification.
 * Delegates to the Classification Registry.
 */
export const getNamesMap = ({ productClass }: { productClass: ProductClass }): Map<string, string> | undefined => {
  try {
    return getClassificationNamesMap(productClass);
  } catch {
    return undefined;
  }
};

export const getCategoriesForChartLegend = ({
  view,
  productClass,
  clusters,
  colorBySelection,
  rcaThreshold,
}: {
  view: UIView;
  productClass: ProductClass;
  clusters?: boolean;
  colorBySelection?: ColorBy;
  rcaThreshold?: number;
}): CategoryDatum[] => {
  let categoriesForLegend: CategoryDatum[] = [];

  if (colorBySelection === ColorBy.Green) {
    categoriesForLegend = getLegendCategories(rcaThreshold!);
  } else if (view === UIView.Markets) {
    categoriesForLegend = [...groupedRegionColorMap.entries()]
      .map((group: [string, string]) => ({
        id: group[0],
        color: group[1],
        name: groupNames.get(group[0]),
      }))
      .sort((a, b) => {
        if (
          a.id === servicesPartnersGroupId ||
          b.id === servicesPartnersGroupId
        ) {
          return a.id === servicesPartnersGroupId ? -1 : 1;
        } else {
          return 0;
        }
      });
  } else if (view === UIView.Products) {
    if (productClass === ProductClass.HS92Products && clusters) {
      categoriesForLegend = [...clusterColorsMap.entries()].map(
        (group: [string, string]) => ({
          id: group[0],
          color: group[1],
          name: group[0].replace(/ Goods/g, "").split(" and ").join(" & "),
        }),
      );
    } else {
      const config = productClassLegendConfig[productClass];
      if (config) {
        categoriesForLegend = [...config.colorMap.entries()]
          .map(([id, color]) => ({ id, color, name: config.namesMap.get(id) }))
          .sort((a, b) =>
            a.id === config.servicesCategory ? -1 : b.id === config.servicesCategory ? 1 : 0,
          );
      }
    }
  }

  return categoriesForLegend;
};

/**
 * Get category sorting order for a product classification.
 * Delegates to the Classification Registry.
 */
export const getCategorySortingOrderFromProductClass = ({
  productClass,
}: {
  productClass: ProductClass;
}): string[] => {
  return getClassificationSortingOrder(productClass);
};

export const getRegionSortingOrder = () => {
  let sortingOrder: string[] = [
    "group-2",
    "group-19",
    "group-142",
    "group-150",
    "group-9",
    "group-998",
  ];
  return sortingOrder;
};

/**
 * Get services category ID for a product classification.
 * Delegates to the Classification Registry.
 */
export const getProductClassServicesCategoryId = ({
  productClass,
}: {
  productClass: ProductClass;
}): string | undefined => {
  return getClassificationServicesCategory(productClass);
};
